"use client"

import { HttpTypes } from "@medusajs/types"
import { captureAnalyticsEvent } from "@/lib/analytics/posthog-client"
import { Text, Button } from "@medusajs/ui"
import { ShoppingCart, ExclamationCircle } from "@medusajs/icons"
import LocalizedClientLink from "@/modules/common/components/localized-client-link"
import { convertToLocale } from "@/lib/util/money"
import { addToCartEventBus } from "@/lib/data/cart-event-bus"
import { useState } from "react"
import type { SearchResultsBlock } from "../../templates/search-template"

type SearchProductListItemProps = {
  product: any // MeiliSearch hit
  countryCode: string
  customer?: HttpTypes.StoreCustomer | null
  searchResultsClickSurface?: "search_grid" | "search_list"
  /** /search block the item sits in, sent as `result_block` with the click. */
  searchResultsBlock?: SearchResultsBlock
}

/** Check if product is a bestseller from MeiliSearch hit metadata (matches getBestsellerProductIds filter logic) */
function isBestseller(product: { metadata?: Record<string, unknown> }): boolean {
  const meta = product?.metadata
  if (!meta) return false
  const bs = meta.bestseller
  const bss = meta.bestsellerStringValue
  if (bs && ["yes", "true", "1"].includes(String(bs).toLowerCase())) return true
  if (bss === 1 || bss === "1" || (bss && ["yes", "true"].includes(String(bss).toLowerCase()))) return true
  return false
}

export default function SearchProductListItem({
  product,
  countryCode,
  customer,
  searchResultsClickSurface,
  searchResultsBlock,
}: SearchProductListItemProps) {
  const [isAddingToCart, setIsAddingToCart] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)

  // Get the cheapest price from the product's prices array
  const getPrice = () => {
    if (!product.prices || !Array.isArray(product.prices) || product.prices.length === 0) {
      return null
    }
    
    const cheapestPrice = Math.min(...product.prices.filter((price: number) => price > 0))
    const currencyCode = "eur"
    
    return { amount: cheapestPrice, currency_code: currencyCode }
  }

  const getDeliveryInfo = () => {
    if (!product.variant_inventory || !Array.isArray(product.variant_inventory)) {
      return { hasStock: false, deliveryMessage: "", quantity: 0 }
    }

    let krekStock = 0
    let otherWarehouseStock = 0
    let shortestDeliveryDays = Infinity
    let bestWarehouse = ""

    // Check all variants for stock information
    product.variant_inventory.forEach((variantInv: any) => {
      if (!variantInv.stock_locations || !Array.isArray(variantInv.stock_locations)) {
        return
      }

      variantInv.stock_locations.forEach((location: any) => {
        const available = location.available_quantity || 0
        
        if (available > 0) {
          if (location.location_name === "Krek") {
            krekStock += available
          } else {
            otherWarehouseStock += available
            // Check delivery time from metadata
            const deliveryDays = location.location_metadata?.dobavni_rok 
              ? parseInt(location.location_metadata.dobavni_rok) 
              : Infinity
            
            if (deliveryDays < shortestDeliveryDays) {
              shortestDeliveryDays = deliveryDays
              bestWarehouse = location.location_name
            }
          }
        }
      })
    })

    // Determine delivery message based on priority: Krek first, then shortest delivery time
    if (krekStock > 0) {
      return {
        hasStock: true,
        deliveryMessage: "Na voljo takoj",
        quantity: krekStock,
        isKrek: true
      }
    } else if (otherWarehouseStock > 0 && shortestDeliveryDays !== Infinity) {
      return {
        hasStock: true,
        deliveryMessage: `Dostava v ${shortestDeliveryDays} dneh`,
        quantity: otherWarehouseStock,
        isKrek: false
      }
    }

    return { hasStock: false, deliveryMessage: "", quantity: 0, isKrek: false }
  }

  // Handle add to cart functionality
  const handleAddToCart = async () => {
    if (!product.variants || product.variants.length === 0) {
      console.error("No variants available for this product")
      return
    }

    // Get the first available variant (you might want to implement variant selection)
    const variant = product.variants[0]
    
    if (!variant.id) {
      console.error("Variant ID not found")
      return
    }

    setIsAddingToCart(true)

    try {
      addToCartEventBus.emitCartAdd({
        lineItems: [
          {
            productVariant: {
              ...variant,
              product: {
                id: product.id,
                title: product.title,
                handle: product.handle,
                thumbnail: product.thumbnail,
                description: product.description,
                // Add other product fields as needed
              },
            },
            quantity: 1,
          },
        ],
        regionId: "", // This will be handled by the cart context
      })
    } catch (error) {
      console.error("Failed to add to cart:", error)
    } finally {
      setIsAddingToCart(false)
    }
  }

  const price = getPrice()
  const deliveryInfo = getDeliveryInfo()
  const thumbnail = product.thumbnail || product.images?.[0]?.url
  const showBestsellerTag = isBestseller(product)
  const isB2BOnly = product.exists_on_trgovina === false
  const handleResultClick = () => {
    if (!searchResultsClickSurface) {
      return
    }
    captureAnalyticsEvent("search_results_clicked", {
      product_id: product.id,
      product_title: product.title,
      product_handle: product.handle,
      result_surface: searchResultsClickSurface,
      result_block: searchResultsBlock,
      destination: isB2BOnly ? "external_b2b" : "product_page",
    })
  }

  // Check if stock is limited (under 10 items)
  const isLimitedStock = deliveryInfo.hasStock && deliveryInfo.quantity > 0 && deliveryInfo.quantity <= 10

  // Handle image load events
  const handleImageLoad = () => {
    setImageLoaded(true)
    setImageError(false)
  }

  const handleImageError = () => {
    setImageError(true)
    setImageLoaded(false)
  }

  const productLink = isB2BOnly
    ? `https://horeca.krek.si/si/products/${product.handle}?ref=trgovina_search`
    : undefined

  const ImageLink = ({ children }: { children: React.ReactNode }) => {
    if (isB2BOnly) {
      return (
        <a href={productLink} target="_blank" rel="noopener noreferrer" onClick={handleResultClick}>
          {children}
        </a>
      )
    }
    return (
      <LocalizedClientLink href={`/products/${product.handle}`} onClick={handleResultClick}>
        {children}
      </LocalizedClientLink>
    )
  }

  const TitleLink = ({ children }: { children: React.ReactNode }) => {
    if (isB2BOnly) {
      return (
        <a href={productLink} target="_blank" rel="noopener noreferrer" onClick={handleResultClick}>
          {children}
        </a>
      )
    }
    return (
      <LocalizedClientLink href={`/products/${product.handle}`} onClick={handleResultClick}>
        {children}
      </LocalizedClientLink>
    )
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm border ${isB2BOnly ? 'border-amber-300' : 'border-neutral-200'} p-6 hover:shadow-md transition-all duration-200`}>
      <div className="flex flex-col md:flex-row gap-6">
        {/* Product Image */}
        <div className="md:w-32 md:h-32 w-full h-48 flex-shrink-0">
          <ImageLink>
            <div className="relative w-full h-full overflow-hidden rounded-lg bg-gradient-to-br from-neutral-50 to-neutral-100 group">
              {isB2BOnly && (
                <div className="absolute top-2 left-2 z-10">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-semibold bg-amber-500 text-white shadow-sm">
                    Veleprodaja
                  </span>
                </div>
              )}
              {showBestsellerTag && !isB2BOnly && (
                <div className="absolute top-2 left-2 z-10">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-semibold bg-amber-500 text-white shadow-sm">
                    Najbolj priljubljeno
                  </span>
                </div>
              )}
              {thumbnail && !imageError ? (
                <>
                  {/* Loading placeholder */}
                  {!imageLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center bg-neutral-100">
                      <div className="animate-pulse w-6 h-6 border-2 border-neutral-300 border-t-neutral-600 rounded-full"></div>
                    </div>
                  )}
                  
                  {/* Main image */}
                  <img
                    src={thumbnail}
                    alt={product.title}
                    className={`w-full h-full object-cover transition-opacity duration-300 ${
                      imageLoaded ? 'opacity-100' : 'opacity-0'
                    }`}
                    loading="lazy"
                    onLoad={handleImageLoad}
                    onError={() => {
                      handleImageError()
                      captureAnalyticsEvent("image_load_failed", {
                        product_id: product.id,
                        product_handle: product.handle,
                        image_url: thumbnail,
                        surface: "search_list",
                      })
                    }}
                  />
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-neutral-100">
                  <Text className="text-neutral-400 text-2xl">📦</Text>
                </div>
              )}
              
              {/* Delivery Badge */}
              {deliveryInfo.hasStock && deliveryInfo.deliveryMessage && (
                <div className="absolute top-2 right-2">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      deliveryInfo.isKrek
                        ? "bg-green-100 text-green-800"
                        : "bg-blue-100 text-blue-800"
                    }`}
                  >
                    {deliveryInfo.deliveryMessage}
                  </span>
                </div>
              )}
            </div>
          </ImageLink>
        </div>

        {/* Product Info */}
        <div className="flex-1 space-y-4">
          <TitleLink>
            <Text className="text-xl font-semibold text-neutral-800 hover:text-blue-600 transition-colors" title={product.title}>
              {product.title}
            </Text>
          </TitleLink>

          {/* Limited Stock Warning */}
          {isLimitedStock && (
            <div className="flex items-start gap-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <ExclamationCircle className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <Text className="text-sm font-medium text-orange-800">
                  Omejena zaloga!
                </Text>
                <Text className="text-xs text-orange-700">
                  Samo {deliveryInfo.quantity} preostane - pohitite!
                </Text>
              </div>
            </div>
          )}

          {/* Additional Product Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            {product.type && (
              <div>
                <Text className="text-xs text-neutral-500 uppercase tracking-wider">Tip</Text>
                <Text className="text-neutral-700">{product.type.value}</Text>
              </div>
            )}
            
            {product.material && (
              <div>
                <Text className="text-xs text-neutral-500 uppercase tracking-wider">Material</Text>
                <Text className="text-neutral-700 capitalize">{product.material}</Text>
              </div>
            )}
            
            <div>
              <Text className="text-xs text-neutral-500 uppercase tracking-wider">Zaloga</Text>
              <Text className={`text-neutral-700 ${isLimitedStock ? 'font-semibold text-orange-600' : ''}`}>
                {deliveryInfo.isKrek && deliveryInfo.quantity > 0 ? `${deliveryInfo.quantity} na voljo` : ''}
              </Text>
            </div>
            

          </div>
        </div>

        {/* Price & Actions */}
        <div className="md:w-48 flex flex-col justify-between space-y-4">
          {/* Price */}
          <div className="text-right">
            {price ? (
              <Text className="text-lg font-semibold text-neutral-800">
                {convertToLocale({ amount: price.amount, currency_code: price.currency_code })}
              </Text>
            ) : (
              <Text className="text-neutral-500">Zahtevaj ponudbo za ceno</Text>
            )}
          </div>

          {/* Stock Info - Only show if Krek warehouse has stock */}
          {deliveryInfo.isKrek && deliveryInfo.quantity > 0 && (
            <div className="text-right">
              <Text className={`text-xs ${isLimitedStock ? 'text-orange-600 font-semibold' : 'text-neutral-600'}`}>
                {deliveryInfo.quantity} na zalogi
              </Text>
            </div>
          )}

          {/* Actions */}
          {isB2BOnly ? (
            <div className="space-y-3">
              <a
                href={productLink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-amber-500 hover:bg-amber-600 text-white transition-colors"
              >
                Poglej na Horeca Krek
              </a>
            </div>
          ) : deliveryInfo.hasStock ? (
            <div className="space-y-3">
              <Button
                disabled={isAddingToCart}
                className={`w-full flex items-center justify-center gap-2 ${
                  isLimitedStock
                    ? "bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                    : "bg-terracotta hover:bg-terracotta-hover text-white"
                }`}
                onClick={handleAddToCart}
                isLoading={isAddingToCart}
              >
                <ShoppingCart className="w-4 h-4" />
                {isLimitedStock ? "Kupi zdaj - omejena zaloga!" : "Dodaj v košarico"}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
} 